import { FormEvent, useState } from "react";
import { useParams } from "react-router-dom";
import { Send } from "lucide-react";
import { EmptyState, ErrorState, LoadingState } from "../../components/LoadingState";
import { NewsletterBox } from "../../components/NewsletterBox";
import { Seo } from "../../components/Seo";
import { useApi } from "../../hooks/useApi";
import { apiSend } from "../../services/api";

type Page = {
  title: string;
  content: string;
  seo_title?: string;
  seo_description?: string;
};

export function StaticPage({ slug: fixedSlug }: { slug?: string }) {
  const params = useParams();
  const slug = fixedSlug ?? params.slug ?? "about";
  const { data, loading, error } = useApi<Page | null>(`/api/content/page/${slug}`, [slug]);

  if (slug === "newsletter") return <NewsletterOnlyPage />;
  if (slug === "contact") return <ContactPage />;
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState title="Page unavailable" body="This page has not been published yet." />;

  return (
    <>
      <Seo title={data.seo_title || data.title} description={data.seo_description} />
      <div className="page-wrap static-page">
        <header className="page-heading">
          <span className="eyebrow">ZimSmart</span>
          <h1>{data.title}</h1>
        </header>
        <div className="article-content" dangerouslySetInnerHTML={{ __html: data.content }} />
      </div>
    </>
  );
}

function NewsletterOnlyPage() {
  return (
    <div className="page-wrap">
      <Seo title="Newsletter | ZimSmart" />
      <NewsletterBox />
    </div>
  );
}

function ContactPage() {
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("saving");
    const form = new FormData(event.currentTarget);
    try {
      await apiSend("/api/contact", {
        method: "POST",
        body: JSON.stringify(Object.fromEntries(form.entries()))
      });
      event.currentTarget.reset();
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  }

  return (
    <div className="page-wrap static-page">
      <Seo title="Contact | ZimSmart" />
      <header className="page-heading">
        <span className="eyebrow">Contact</span>
        <h1>Get In Touch</h1>
      </header>
      <form className="panel-form" onSubmit={submit}>
        <input name="website" className="honeypot" tabIndex={-1} autoComplete="off" />
        <label>
          Name
          <input name="name" required />
        </label>
        <label>
          Email
          <input name="email" type="email" required />
        </label>
        <label>
          Subject
          <input name="subject" required />
        </label>
        <label>
          Message
          <textarea name="message" rows={7} required />
        </label>
        <button type="submit" className="button">
          <Send size={16} />
          {status === "saving" ? "Sending..." : "Send Message"}
        </button>
        <p aria-live="polite">{status === "saved" ? "Message saved." : status === "error" ? "Message could not be sent." : ""}</p>
      </form>
    </div>
  );
}
